/**
 * Conditional visibility rules for checklist definitions. A rule is one
 * element of the `visibilityRules` array stored on the conditioned item:
 *   { triggerItemId, optionIds: [optionId, ...] } — option triggers (radios,
 *     select, checkboxes); the condition is met when at least one option is
 *     selected.
 *   { triggerItemId, range: { min, max } } — range triggers (number, scale),
 *     inclusive bounds.
 *
 * This module normalizes the save payload so editor state never reaches the
 * saved document; runtime evaluation lives in frontend-js-shared.
 */
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import { MAX_VISIBILITY_RULES } from '@/components/Checklists/ChecklistCreation/constants'

import { toNullableNumber } from '../utils'

function getTriggerItemId(rule) {
  return utils.getEntityId(rule.triggerItemId) ?? null
}

function normalizeOptionIds(optionIds) {
  if (!_.isArray(optionIds)) {
    return null
  }

  const uniqueOptionIds = [
    ...new Set(
      optionIds.map(optionId => utils.getEntityId(optionId)).filter(Boolean)
    ),
  ]

  return _.isEmpty(uniqueOptionIds) ? null : uniqueOptionIds
}

function normalizeRange(range) {
  if (!_.isPlainObject(range)) {
    return null
  }

  const normalizeBound = value =>
    _.isNumber(value) || (_.isString(value) && !_.isEmpty(value.trim()))
      ? toNullableNumber(value)
      : null
  const min = normalizeBound(range.min)
  const max = normalizeBound(range.max)

  if (min === null || max === null || min > max) {
    return null
  }

  return { min, max }
}

// Save-ready visibility rule: only the contract fields — a normalized
// triggerItemId plus either normalized optionIds or a numeric range. Returns
// null when the rule is absent, ambiguous or incomplete, so editor state
// never leaks into the saved document.
export default function normalizeVisibilityRulePayload(rule) {
  if (!_.isPlainObject(rule)) {
    return null
  }

  const triggerItemId = getTriggerItemId(rule)
  if (!triggerItemId) {
    return null
  }

  const hasOptionIds = _.isArray(rule.optionIds)
  const hasRange = _.isPlainObject(rule.range)

  if (hasOptionIds === hasRange) {
    return null
  }

  if (hasOptionIds) {
    const optionIds = normalizeOptionIds(rule.optionIds)
    return optionIds ? { triggerItemId, optionIds } : null
  }

  const range = normalizeRange(rule.range)
  return range ? { triggerItemId, range } : null
}

// Save-ready `visibilityRules` array: every valid rule normalized, capped at
// the contract maximum. Always returns an array — `[]` when the item has no
// valid rules — so unconditioned items carry the BE-confirmed empty shape.
export function normalizeVisibilityRulesPayload(rules) {
  if (!_.isArray(rules)) {
    return []
  }

  return rules
    .map(normalizeVisibilityRulePayload)
    .filter(rule => !_.isNull(rule))
    .slice(0, MAX_VISIBILITY_RULES)
}
